import type {
  WesApiCounts,
  WesApiDetailPage,
  WesApiDetailRow,
  WesApiGeography,
  WesApiGroup,
  WesApiRefreshStatus,
  WesApiTrend,
} from './wesPalletStagingApi';
import type { WesStagingScope, WesTrend } from './wesPalletStagingStaticRows';
import type { WesDimKey, WesDisplayMode, WesMetricKey } from './wesPalletStagingTable';
import { formatToLocalWithTZ } from '../../constants/dateHelpers';

// Display helpers for API values: percentages arrive unrounded (0-100); null means unavailable and shows as -.
export const formatApiPercent = (pct: number | null | undefined): string =>
  pct === null || pct === undefined ? '-' : `${(Math.round(pct * 10) / 10).toFixed(1)}%`;

export const formatApiCount = (value: number | null | undefined): string =>
  value === null || value === undefined ? '-' : value.toLocaleString('en-US');

export const AUDIT_SPLIT_NOTE = 'Audit split not available yet';
export const AUDIT_RELATED_EXPLANATION = 'Quality Audits plus Short Audits equals Staged (Audit-Related)';

const trimZeros = (s: string) => (s.includes('.') ? s.replace(/0+$/, '').replace(/\.$/, '') : s);

// Mockup fmtK: M means thousands and MM millions; the full count is shown next to it.
export const formatCompactCount = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return '-';
  if (value >= 999500) return `${trimZeros((value / 1e6).toFixed(2))}MM`;
  if (value >= 1000) return `${trimZeros((value / 1000).toFixed(value >= 100000 ? 0 : value >= 10000 ? 1 : 2))}M`;
  return value.toLocaleString('en-US');
};

export const stagedForScope = (counts: WesApiCounts, scope: WesStagingScope): number | null =>
  scope === 'audit' ? counts.stagedAuditRelated : scope === 'other' ? counts.stagedOther : counts.staged;

export const isAuditSplitMissing = (counts: WesApiCounts | null | undefined): boolean =>
  !!counts && (counts.stagedAuditRelated === null || counts.stagedOther === null);

export interface WesLocationHighlight {
  label: string;
  pct: number | null;
  staged: number | null;
  total: number;
}

export const toHighlight = (group: WesApiGroup | null | undefined, scope: WesStagingScope): WesLocationHighlight | null =>
  group ? { label: group.label, pct: group.stagedPct, staged: stagedForScope(group.counts, scope), total: group.counts.total } : null;

export interface WesGeoBar {
  key: string;
  label: string;
  pct: number;
  staged: number | null;
  total: number;
}

// Groups without a percent (no pallets, or the scope value is unavailable) are not ranked.
export const toGeoBars = (geography: WesApiGeography, scope: WesStagingScope): WesGeoBar[] =>
  geography.groups
    .filter((g) => g.stagedPct !== null)
    .map((g) => ({
      key: g.key,
      label: g.label,
      pct: g.stagedPct as number,
      staged: stagedForScope(g.counts, scope),
      total: g.counts.total,
    }));

// Points are matched to buckets by label; a bucket with no point or a null percent is a gap.
// With capAt, only the series with the most pallets are kept (then ordered by staged percent, highest first), as in the mockup at Location grain.
export interface WesTrendView extends WesTrend {
  shown: number;
  available: number;
}

export const WES_TREND_LOCATION_CAP = 10;

export const toTrend = (trend: WesApiTrend, scope: WesStagingScope, capAt?: number): WesTrendView => {
  const all = trend.series.map((s) => {
    const byBucket = new Map(s.points.map((p) => [p.bucket, p]));
    const values: (number | null)[] = [];
    const counts: ({ staged: number; total: number } | null)[] = [];
    trend.buckets.forEach((bucket) => {
      const point = byBucket.get(bucket);
      const staged = point ? stagedForScope(point.counts, scope) : null;
      if (!point || point.stagedPct === null || staged === null) {
        values.push(null);
        counts.push(null);
      } else {
        values.push(point.stagedPct);
        counts.push({ staged, total: point.counts.total });
      }
    });
    const totalPallets = s.points.reduce((sum, p) => sum + p.counts.total, 0);
    const stagedPallets = s.points.reduce((sum, p) => sum + (stagedForScope(p.counts, scope) ?? 0), 0);
    return { label: s.label, values, counts, totalPallets, pct: totalPallets > 0 ? stagedPallets / totalPallets : 0 };
  });

  const capped = capAt !== undefined && all.length > capAt;
  const kept = capped ? [...all].sort((a, b) => b.totalPallets - a.totalPallets).slice(0, capAt).sort((a, b) => b.pct - a.pct) : all;
  return {
    buckets: trend.buckets.map((label, index) => ({ key: label, label, order: index })),
    series: kept.map(({ label, values, counts }) => ({ label, values, counts })),
    shown: kept.length,
    available: all.length,
  };
};

// ─── Location Detail ─────────────────────────────────────────────────────────────────────────────

const DIM_FROM_API: Record<string, WesDimKey> = {
  REGION: 'region',
  MARKET: 'market',
  LOCATION: 'location',
  LOCATION_ID: 'locationId',
  YEAR: 'year',
  PERIOD: 'period',
  WEEK: 'week',
};

const DIM_TEXT: Record<WesDimKey, (value: string) => string> = {
  region: (v) => v,
  market: (v) => v,
  location: (v) => v,
  locationId: (v) => v,
  year: (v) => v,
  period: (v) => `P${v}`,
  week: (v) => `Week ${v}`,
};

// Ratio of summed counts; the page totals come as counts only.
const ratioPct = (numerator: number | null, total: number): number | null => (numerator === null || total === 0 ? null : (numerator * 100) / total);

export interface WesDetailRowView {
  id: string;
  text: Partial<Record<WesDimKey, string>>;
  // Display strings for the four metric columns in the current mode.
  metrics: Record<WesMetricKey, string>;
  // Raw values for the export: counts, and percents of Total Pallets (null where unavailable).
  counts: Record<WesMetricKey, number | null>;
  pcts: Record<WesMetricKey, number | null>;
  auditTip: string;
}

const metricsOf = (counts: WesApiCounts, pcts: { staged: number | null; other: number | null; audit: number | null }, mode: WesDisplayMode) => {
  const rawCounts: Record<WesMetricKey, number | null> = { total: counts.total, staged: counts.staged, other: counts.stagedOther, audit: counts.stagedAuditRelated };
  const rawPcts: Record<WesMetricKey, number | null> = { total: null, staged: pcts.staged, other: pcts.other, audit: pcts.audit };
  const show = (key: WesMetricKey) =>
    key === 'total' || mode === 'pallets' ? formatApiCount(rawCounts[key]) : formatApiPercent(rawPcts[key]);
  const showAudit = (value: number | null | undefined) =>
    mode === 'pallets' ? formatApiCount(value) : formatApiPercent(ratioPct(value ?? null, counts.total));
  return {
    counts: rawCounts,
    pcts: rawPcts,
    metrics: { total: show('total'), staged: show('staged'), other: show('other'), audit: show('audit') } as Record<WesMetricKey, string>,
    auditTip:
      counts.stagedAuditRelated === null
        ? AUDIT_SPLIT_NOTE
        : `${showAudit(counts.qualityAudit)} quality audit pallets | ${showAudit(counts.shortAudit)} short audit pallets`,
  };
};

export const toDetailRow = (row: WesApiDetailRow, index: number, mode: WesDisplayMode): WesDetailRowView => {
  const text: Partial<Record<WesDimKey, string>> = {};
  Object.entries(row.dimensions).forEach(([name, value]) => {
    const key = DIM_FROM_API[name];
    if (key) text[key] = DIM_TEXT[key](value);
  });
  return {
    id: Object.values(row.dimensions).join('|') || String(index),
    text,
    ...metricsOf(row.counts, { staged: row.stagedPct, other: row.stagedOtherPct, audit: row.stagedAuditRelatedPct }, mode),
  };
};

export const toTotalsRow = (totals: WesApiCounts, mode: WesDisplayMode): WesDetailRowView => ({
  id: 'total',
  text: {},
  ...metricsOf(
    totals,
    { staged: ratioPct(totals.staged, totals.total), other: ratioPct(totals.stagedOther, totals.total), audit: ratioPct(totals.stagedAuditRelated, totals.total) },
    mode,
  ),
});

export interface WesDetailView {
  rows: WesDetailRowView[];
  totals: WesDetailRowView;
  totalGroups: number;
  pageSize: number;
}

export const toDetailView = (page: WesApiDetailPage, mode: WesDisplayMode): WesDetailView => ({
  rows: page.rows.map((row, index) => toDetailRow(row, index, mode)),
  totals: toTotalsRow(page.totals, mode),
  totalGroups: page.totalGroups,
  pageSize: page.size,
});

// ─── Refresh status ──────────────────────────────────────────────────────────────────────────────

const parseDateOnly = (value: string): Date | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  return match ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])) : null;
};

// Instant shown in the device's own timezone; the data-through date is a plain calendar date.
export const formatRefreshStatus = (status: WesApiRefreshStatus): { value: string; through: string } | null => {
  if (!status.lastRefreshedAt) return null;
  const at = new Date(status.lastRefreshedAt);
  if (Number.isNaN(at.getTime())) return null;
  const through = status.dataThroughDate ? parseDateOnly(status.dataThroughDate) : null;
  return {
    value: formatToLocalWithTZ(status.lastRefreshedAt),
    through: through ? through.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' }) : '',
  };
};
